import React, { useState, useCallback } from 'react';
import TestManager from './screens/TestManager';
import HomeScreen from './screens/HomeScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import SettingsScreen from './screens/SettingsScreen';
import LandingPage from './screens/LandingPage';
import { Project, TestResult } from './types';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import TestInfoModal from './components/TestInfoModal';
import CSVEditorModal from './components/CSVEditorModal';
import ScenarioEditorModal from './components/ScenarioEditorModal';
import { ToastProvider } from './contexts/ToastContext';
import AnalysisContainer from './components/AnalysisContainer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectsProvider, useProjects } from './contexts/ProjectsContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { TestSessionProvider, useTestSession } from './contexts/TestSessionContext';
import { useHandleNavigate } from './hooks/useHandleNavigate';
import { useUrlSync } from './hooks/useUrlSync';

const API_BASE_URL = process.env.REACT_APP_API_HOST;

/** Wraps children with TestSessionProvider using selectedProject from ProjectsContext */
function TestSessionWrapper({ children }: { children: React.ReactNode }) {
  const { selectedProject } = useProjects();
  return <TestSessionProvider selectedProject={selectedProject}>{children}</TestSessionProvider>;
}

/** Inner app that has access to all contexts; runs URL sync and renders screens */
function AppContent() {
  const { isAuthenticated, currentUser, login, logout, register } = useAuth();
  const {
    projects,
    selectedProject,
    setSelectedProject,
    handleProjectsUpdate,
  } = useProjects();
  const {
    currentScreen,
    setCurrentScreen,
    previousScreen,
    isAnalyzing,
    setIsAnalyzing,
  } = useNavigation();
  const session = useTestSession();
  const {
    scenarios,
    categories,
    currentScenario,
    currentTest,
    testResults,
    selectedFile,
    loadScenarios,
    loadTestResults,
    handleStartNewTest,
    handleNewTestClick,
    handleTestInfoSubmit,
    continueTest,
    handleStatusChange,
    handleNotesChange,
    handleCategoriesUpdate,
    handleEditCSV,
    handleSaveCSV,
    handleEditScenario,
    handleSaveScenario,
    handleAddScenario,
    handleTestUpdate,
    handleDeleteTest,
    handleDeleteScenario,
    handleDeleteCategory,
    handleScenarioSelect,
    extractCategories,
    isTestInfoModalOpen,
    setIsTestInfoModalOpen,
    isCSVEditorOpen,
    setIsCSVEditorOpen,
    csvContent,
    isScenarioEditorOpen,
    setIsScenarioEditorOpen,
    scenarioToEdit,
  } = session;

  const { handleNavigate } = useHandleNavigate();
  useUrlSync();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      try {
        await login(email, password);
        setCurrentScreen('home');
        window.history.pushState({}, '', '/home');
      } catch (error) {
        let errorMessage = 'Wystąpił błąd podczas logowania';
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          const isHttps = API_BASE_URL?.startsWith('https://');
          if (isHttps) {
            if (API_BASE_URL?.includes(':3003') || API_BASE_URL?.includes('162.19.245.133')) {
              errorMessage =
                'Błąd konfiguracji: Aplikacja używa starego adresu serwera. Proszę przebudować aplikację z poprawnym URL: https://testmanager.mocnetestowanie.pl';
            } else {
              errorMessage = `Błąd połączenia SSL: Nie można połączyć się z ${API_BASE_URL}. Sprawdź certyfikat serwera lub skontaktuj się z administratorem.`;
            }
          } else {
            errorMessage =
              'Błąd połączenia: Nie można połączyć się z serwerem. Sprawdź połączenie sieciowe.';
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        alert(errorMessage);
      }
    },
    [login, setCurrentScreen]
  );

  const handleRegister = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      try {
        await register(email, password, firstName, lastName);
        setCurrentScreen('home');
        window.history.pushState({}, '', '/home');
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Wystąpił błąd podczas rejestracji');
      }
    },
    [register, setCurrentScreen]
  );

  const handleContinueTest = useCallback(
    async (result: TestResult) => {
      await continueTest(result);
      if (selectedProject) {
        window.history.pushState({}, '', `/test/${selectedProject.id}/${result.metadata.id}`);
      } else {
        window.history.pushState({}, '', `/test/${result.metadata.id}`);
      }
    },
    [continueTest, selectedProject]
  );

  const handleProjectSelect = useCallback(
    (project: Project | null) => {
      setSelectedProject(project);
      session.setSelectedFile(null);
      if (project?.csvFiles?.length) {
        const first = project.csvFiles[0];
        session.setSelectedFile(new File([], first.name));
        loadScenarios(first.originalName);
      }
    },
    [setSelectedProject, session, loadScenarios]
  );

  const handleAnalyzeProject = useCallback(
    (project: Project) => {
      setSelectedProject(project);
      setIsAnalyzing(true);
      setCurrentScreen('home');
      window.history.pushState({}, '', `/analysis/${project.id}`);
    },
    [setSelectedProject, setIsAnalyzing, setCurrentScreen]
  );

  const handleBackFromAnalysis = useCallback(() => {
    setIsAnalyzing(false);
    switch (currentScreen) {
      case 'test':
        if (currentTest) {
          if (selectedProject) {
            window.history.pushState({}, '', `/test/${selectedProject.id}/${currentTest.metadata.id}`);
          } else {
            window.history.pushState({}, '', `/test/${currentTest.metadata.id}`);
          }
        }
        break;
      case 'home':
        window.history.pushState({}, '', '/home');
        break;
      case 'settings':
        window.history.pushState({}, '', '/settings');
        break;
      default:
        window.history.pushState({}, '', '/home');
    }
  }, [currentScreen, currentTest, selectedProject, setIsAnalyzing]);

  const renderScreen = () => {
    if (!isAuthenticated) {
      const path = window.location.pathname;
      if (path === '/register') {
        window.history.replaceState({}, '', '/login');
        return <LoginScreen onLogin={handleLogin} />;
      }
      if (path === '/login') {
        return <LoginScreen onLogin={handleLogin} />;
      }
      return (
        <LandingPage
          onLogin={() => {
            window.history.pushState({}, '', '/login');
            setCurrentScreen('login');
          }}
        />
      );
    }

    switch (currentScreen) {
      case 'login':
        return <LoginScreen onLogin={handleLogin} />;
      case 'register':
        return <RegisterScreen onRegister={handleRegister} />;
      case 'home':
        return (
          <HomeScreen
            projects={projects}
            selectedProject={selectedProject}
            selectedFile={selectedFile}
            testResults={testResults}
            scenarios={scenarios}
            onProjectSelect={handleProjectSelect}
            onFileSelect={handleStartNewTest}
            onNewTestClick={handleNewTestClick}
            onContinueTest={handleContinueTest}
            onProjectsUpdate={handleProjectsUpdate}
            username={currentUser.firstName}
            onAnalyzeProject={handleAnalyzeProject}
            onNavigate={handleNavigate}
            onEditCSV={handleEditCSV}
          />
        );
      case 'test':
        return currentTest ? (
          <TestManager
            categories={categories}
            currentTest={currentTest}
            currentScenario={currentScenario}
            selectedProject={selectedProject}
            onBack={() => {
              session.setCurrentScenario(null);
              session.setCurrentTest(null);
              setCurrentScreen('home');
              loadTestResults();
              window.history.pushState({}, '', '/home');
            }}
            onStatusChange={handleStatusChange}
            onNotesChange={handleNotesChange}
            onScenarioSelect={handleScenarioSelect}
            onCategoriesUpdate={handleCategoriesUpdate}
            onEditCSV={handleEditCSV}
            onEditScenario={handleEditScenario}
            onAddScenario={handleAddScenario}
            onTestUpdate={handleTestUpdate}
            onDeleteTest={handleDeleteTest}
            onDeleteScenario={handleDeleteScenario}
            onDeleteCategory={handleDeleteCategory}
          />
        ) : null;
      case 'settings':
        return <SettingsScreen onBack={() => setCurrentScreen(previousScreen)} />;
      case 'analysis':
        return (
          <>
            <Navbar
              currentTest={null}
              hasActiveTest={false}
              onBack={handleBackFromAnalysis}
              username={currentUser.firstName}
              onLogout={logout}
              onVersionClick={() => {
                const lastTestId = localStorage.getItem('lastTestId');
                if (lastTestId && selectedProject) handleNavigate('test', lastTestId, selectedProject.id);
              }}
              onLastTestClick={() => {
                const lastTestId = localStorage.getItem('lastTestId');
                if (lastTestId && selectedProject) handleNavigate('test', lastTestId, selectedProject.id);
              }}
              onLogoClick={() => handleNavigate('home')}
              isAnalyzing={isAnalyzing}
            />
            <button
              onClick={() => setIsSidebarOpen(true)}
              className={`fixed top-20 left-4 z-40 p-2 rounded-lg bg-white dark:bg-slate-700 shadow-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors ${isSidebarOpen ? 'hidden' : ''}`}
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} onNavigate={handleNavigate} />
            {selectedProject && (
              <AnalysisContainer
                project={selectedProject}
                onBack={handleBackFromAnalysis}
                username={currentUser.firstName}
                onLogout={logout}
                onNavigate={handleNavigate}
              />
            )}
          </>
        );
      default:
        return null;
    }
  };

  if (isAnalyzing && selectedProject) {
    return (
      <>
        <Navbar
          currentTest={null}
          hasActiveTest={false}
          onBack={handleBackFromAnalysis}
          username={currentUser.firstName}
          onLogout={logout}
          onVersionClick={() => {
            const lastTestId = localStorage.getItem('lastTestId');
            if (lastTestId && selectedProject) handleNavigate('test', lastTestId, selectedProject.id);
          }}
          onLastTestClick={() => {
            const lastTestId = localStorage.getItem('lastTestId');
            if (lastTestId && selectedProject) handleNavigate('test', lastTestId, selectedProject.id);
          }}
          onLogoClick={() => handleNavigate('home')}
          isAnalyzing={isAnalyzing}
        />
        <button
          onClick={() => setIsSidebarOpen(true)}
          className={`fixed top-20 left-4 z-40 p-2 rounded-lg bg-white dark:bg-slate-700 shadow-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors ${isSidebarOpen ? 'hidden' : ''}`}
        >
          <svg className="w-6 h-6 text-gray-600 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} onNavigate={handleNavigate} />
        {selectedProject && (
          <AnalysisContainer
            project={selectedProject}
            onBack={handleBackFromAnalysis}
            username={currentUser.firstName}
            onLogout={logout}
            onNavigate={handleNavigate}
          />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {isAuthenticated && !isAnalyzing && (
        <>
          <Navbar
            currentTest={currentTest}
            hasActiveTest={Boolean(currentTest)}
            onBack={() => setCurrentScreen('home')}
            username={currentUser.firstName}
            onLogout={logout}
            onVersionClick={() => {
              const lastTestId = localStorage.getItem('lastTestId');
              if (lastTestId && selectedProject) handleNavigate('test', lastTestId, selectedProject.id);
              else if (lastTestId) handleNavigate('test', lastTestId);
            }}
            onLogoClick={() => handleNavigate('home')}
            onLastTestClick={() => {
              const lastTestId = localStorage.getItem('lastTestId');
              if (lastTestId && selectedProject) handleNavigate('test', lastTestId, selectedProject.id);
              else if (lastTestId) handleNavigate('test', lastTestId);
            }}
            isAnalyzing={isAnalyzing}
          />
          <button
            onClick={() => setIsSidebarOpen(true)}
            className={`fixed top-20 left-4 z-40 p-2 rounded-lg bg-white dark:bg-slate-700 shadow-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors ${isSidebarOpen ? 'hidden' : ''}`}
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} onNavigate={handleNavigate} />
        </>
      )}

      <main className={isAuthenticated ? 'min-h-screen pt-16' : 'min-h-screen'}>{renderScreen()}</main>

      {isAuthenticated && !isAnalyzing && (
        <>
          <TestInfoModal
            isOpen={isTestInfoModalOpen}
            onClose={() => setIsTestInfoModalOpen(false)}
            onSubmit={handleTestInfoSubmit}
            categories={extractCategories(scenarios)}
            username={currentUser.firstName}
          />
          <CSVEditorModal
            isOpen={isCSVEditorOpen}
            onClose={() => setIsCSVEditorOpen(false)}
            content={csvContent}
            onSave={handleSaveCSV}
          />
          <ScenarioEditorModal
            isOpen={isScenarioEditorOpen}
            onClose={() => setIsScenarioEditorOpen(false)}
            scenario={scenarioToEdit}
            onSave={handleSaveScenario}
          />
        </>
      )}
    </div>
  );
}

function AppWithAuth() {
  const { isAuthenticated } = useAuth();
  return (
    <ProjectsProvider isAuthenticated={isAuthenticated}>
      <NavigationProvider>
        <TestSessionWrapper>
          <AppContent />
        </TestSessionWrapper>
      </NavigationProvider>
    </ProjectsProvider>
  );
}

const App: React.FC = () => (
  <ToastProvider>
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  </ToastProvider>
);

export default App;
